import { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { hideBinaryNotification } from '~/components/notifications/ducks/notifications.js';
import useTranslations from '~/components/translation/use-translations.ts';
import { LabelBold } from '@comeon/ui';
import { updateCredentials, signout } from '~/components/authentication/ducks/index.js';
import BinaryQuestion from '~/components/binary-question';
import useUIContext from '~/ui/use-ui-context.ts';
import useTempLoginCompleteVerifications from '~/hooks/use-temp-login-complete-verifications.js';
import { showTempAccountLoginSelector } from '~/components/authentication/ducks/selectors.js';
import { showSupportDialogBox } from '~/components/support/ducks/index.js';
import { getTemporaryPlayerSimple } from './api/index.js';
import { selectIsRegistration } from '../../account/ducks/index.js';
import { insertTrackEvent } from '@comeon/analytics';
import { ROUTE_WELCOME } from '~/components/router/constants.js';

const textKeys = [
	'TEMPORARY_MODAL_TITLE',
	'TEMPORARY_MODAL_SUBTITLE',
	'TEMPORARY_MODAL_VERIFY_LATER',
	'TEMPORARY_MODAL_VERIFY_NOW',
	'DEADLINE_HOUR_MIN_TIME',
	'DEADLINE_HOURS_MIN_TIME',
	'TEMPORARY_MODAL_TITLE_EXPIRED',
	'TEMPORARY_MODAL_SUBTITLE_EXPIRED',
	'TEMPORARY_MODAL_VERIFY_NOW',
	'LOGOUT',
	'MOBILE_RESULT_CONTACT_SUPPORT_BUTTON',
	'OCR_PENDING'
];
// TO MOCK THIS
// const temporaryPlayer = {
// 	startTime: 1666091819291,
// 	remainingTimeInSeconds: 144000,
// 	expired: true,
// 	temporary: true
// };
export default function TemporaryAccountLogin({ expired }) {
	const { translations: t, isLoadingTranslations } = useTranslations(textKeys);
	const dispatch = useDispatch();
	const { withinSidebar } = useUIContext();
	const [shouldOpenBinary, setShouldOpenBinary] = useState(false);
	const [timeLeft, setTimeLeft] = useState({});
	const showTempAccountLogin = useSelector(showTempAccountLoginSelector);
	const isRegistration = useSelector(selectIsRegistration);
	const temporaryPlayer = useSelector(state => state.authentication?.temporaryPlayer);
	const [tempPlayer, setTempPlayer] = useState({});
	const { completeVerificationDecision } = useTempLoginCompleteVerifications();
	const checkTemporary = useCallback(async () => {
		const response = await getTemporaryPlayerSimple();
		if (response.status === 'SUCCESS') {
			setTempPlayer(response.result);
		} else {
			setTempPlayer(temporaryPlayer);
		}
	}, [temporaryPlayer]);

	useEffect(() => {
		// update temporaryPlayer with be api
		checkTemporary();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (shouldOpenBinary) {
			if (expired) {
				insertTrackEvent({
					category: 'Notification',
					action: 'Temporary Account Period Ended Pop-up',
					label: 'Displayed'
				});
			} else {
				insertTrackEvent({
					category: 'Notification',
					action: 'Account Verification Pop-up',
					label: 'Displayed'
				});
			}
		}
	}, [expired, shouldOpenBinary]);

	useEffect(() => {
		if (showTempAccountLogin && tempPlayer?.temporary && !isRegistration) {
			setShouldOpenBinary(true);
		}
	}, [showTempAccountLogin, tempPlayer, isRegistration]);

	const parseSecondsIntoReadableTime = seconds => {
		// Get hours from seconds
		const hours = seconds / 3600;
		const absoluteHours = Math.floor(hours);
		const h = absoluteHours > 9 ? `${absoluteHours}` : `0${absoluteHours}`;

		// Get remainder from hours and convert to minutes
		const minutes = (hours - absoluteHours) * 60;
		const absoluteMinutes = Math.floor(minutes);
		const m = absoluteMinutes > 9 ? `${absoluteMinutes}` : `0${absoluteMinutes}`;

		return { hours: h, minutes: m };
	};

	useEffect(() => {
		if (tempPlayer?.temporary) {
			if (tempPlayer?.remainingTimeInSeconds) {
				const { hours, minutes } = parseSecondsIntoReadableTime(tempPlayer?.remainingTimeInSeconds);
				setTimeLeft({ hours, minutes });
			} else {
				setTimeLeft({ hours: '00', minutes: '00' });
			}
		}
	}, [tempPlayer]);

	const getFinalTranslation = useCallback(() => {
		const textKeyhours = t.DEADLINE_HOURS_MIN_TIME.replace('{hours}', `${timeLeft?.hours} `); // the space is required, don't remove

		const textKeyhour = t.DEADLINE_HOUR_MIN_TIME.replace('{hours}', `${timeLeft?.hours} `); // the space is required, don't remove

		const textKeyFinal =
			timeLeft?.hours > 1
				? textKeyhours.replace('{minutes}', `${timeLeft?.minutes} `) // the space is required, don't remove
				: textKeyhour.replace('{minutes}', `${timeLeft?.minutes} `); // the space is required, don't remove
		return textKeyFinal ?? t.DEADLINE_HOUR_MIN_TIME;
	}, [t.DEADLINE_HOUR_MIN_TIME, t.DEADLINE_HOURS_MIN_TIME, timeLeft]);

	const verifyNowCTA = async () => {
		await completeVerificationDecision(t.OCR_PENDING, ROUTE_WELCOME); // it has to go back to welcome screen
		insertTrackEvent({
			category: 'Notification',
			action: 'Account Verification Pop-up',
			label: 'Click - Verify Now'
		});
	};
	const verifyLaterCTA = () => {
		insertTrackEvent({
			category: 'Notification',
			action: 'Account Verification Pop-up',
			label: 'Click - Verify Later'
		});
		setShouldOpenBinary(false);
		dispatch(hideBinaryNotification());
	};

	if (isLoadingTranslations) {
		return <div className="spinner" />;
	}
	if (expired) {
		return (
			<BinaryQuestion
				shouldOpenBinary={tempPlayer?.temporary && tempPlayer?.expired}
				renderInParent={withinSidebar}
				openInSidebar={withinSidebar}
				disableSidebarScroll
				title={t.TEMPORARY_MODAL_TITLE_EXPIRED}
				text={t.TEMPORARY_MODAL_SUBTITLE_EXPIRED}
				onNoClick={() => {
					// LOGOUT TEMPORARY ACCOUNT EXPIRED
					insertTrackEvent({
						category: 'Notification',
						action: 'Temporary Account Period Ended Pop-up',
						label: 'Click - Continue'
					});
					dispatch(signout({}));
				}}
				noText={t.LOGOUT}
				yesText={t.MOBILE_RESULT_CONTACT_SUPPORT_BUTTON}
				onYesClick={() => {
					// Contact support
					insertTrackEvent({
						category: 'Notification',
						action: 'Temporary Account Period Ended Pop-up',
						label: 'Click - Contact Support'
					});
					dispatch(showSupportDialogBox(true));
					dispatch(hideBinaryNotification());
				}}
				thirdKeyClick={() => {
					// Verify now logic
					dispatch(updateCredentials({ showTempAccountScreen: false }));
				}}
				thirdText={t.TEMPORARY_MODAL_VERIFY_NOW}
			/>
		);
	}

	return (
		<BinaryQuestion
			shouldOpenBinary={shouldOpenBinary}
			renderInParent={withinSidebar}
			openInSidebar={withinSidebar}
			disableSidebarScroll
			title={t.TEMPORARY_MODAL_TITLE}
			text={t.TEMPORARY_MODAL_SUBTITLE}
			onNoClick={verifyNowCTA}
			noText={t.TEMPORARY_MODAL_VERIFY_NOW}
			yesText={t.TEMPORARY_MODAL_VERIFY_LATER}
			onYesClick={verifyLaterCTA}
		>
			<LabelBold className="u-mt1">{getFinalTranslation()}</LabelBold>
		</BinaryQuestion>
	);
}
